import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack";
import { TimeRange } from "@/core/components/ui/time-range-picker/time-range-picker";
import { StatsDisplay } from "@/business-settings/common/stats-display";
import { USER_REPORTING_PRESETS } from "@/business-settings/common/user-reporting-presets";
import { SIDEBAR_MODES, SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { useBusiness } from "@/core/business";
import {
  useGetTechnicians,
  useGetUserStatsByBusinessWithTimeRange,
} from "@/user/query";
import { DetailedUserWithStats } from "@/user/user.d";
import { TechniciansTable } from "./technicians-table";
import referralsOverTime from "@/assets/images/referrals-over-time.png";

export const Technicians = () => {
  const { isSpiffOnly } = useBusiness();
  const { businessId } = useParams();
  const {
    updateSidebar,
    state: { selectedItemId },
  } = useBusinessSettingsSidebar();
  const [timeRange, setTimeRange] = useState<TimeRange | null>(null);

  const { data: technicians = [], isLoading: isLoadingTechnicians } =
    useGetTechnicians(businessId);
  const { data: userStatsByBusiness, isLoading: isLoadingStats } =
    useGetUserStatsByBusinessWithTimeRange(businessId, timeRange);

  const isLoading = isLoadingTechnicians || isLoadingStats;

  const handleSelectTechnician = (technician: DetailedUserWithStats) => {
    updateSidebar({
      section: SIDEBAR_SECTIONS.technicians,
      mode: SIDEBAR_MODES.view,
      selectedItemId: technician.id,
      isOpen: true,
    });
  };

  const rankedTechniciansWithStats = useMemo(
    () =>
      technicians
        .map((technician) => ({
          ...technician,
          stats: userStatsByBusiness?.[technician.id]?.stats,
        }))
        .sort(
          (a, b) => (b.stats?.signup_count ?? 0) - (a.stats?.signup_count ?? 0),
        ),
    [technicians, userStatsByBusiness],
  );

  const totalSignups = useMemo(
    () =>
      rankedTechniciansWithStats.reduce(
        (acc, technician) => acc + (technician.stats?.signup_count ?? 0),
        0,
      ),
    [rankedTechniciansWithStats],
  );

  const filterLabel = timeRange?.label || "All Time";

  return (
    <Stack className="flex h-full flex-col" fullWidth>
      {!isSpiffOnly && (
        <Stack
          direction="row"
          className="mx-auto shrink-0 py-4 pr-5"
          justify="center"
          align="center"
          gap="lg"
        >
          <StatsDisplay
            title="Total Attributed Signups"
            value={totalSignups}
            caption={filterLabel}
            isLoading={isLoading}
            tooltipDescription={
              <>
                Total number of signups attributed to technicians in the range
                of <b>{filterLabel}</b>. This may differ from the main dashboard
                which shows all signups.
              </>
            }
          />

          <Separator orientation="vertical" className="h-[128px]" />
          <StatsDisplay
            title="Avg. by tech"
            value={(totalSignups / technicians.length || 0).toFixed(1)}
            caption={filterLabel}
            isLoading={isLoading}
            tooltipDescription={
              <>
                Average number of attributed signups per technician in the range
                of <b>{filterLabel}</b>. This is calculated using{" "}
                <b>total attributed signups</b> divided by{" "}
                <b>number of technicians</b>.
              </>
            }
          />
          <Separator orientation="vertical" className="h-[128px]" />
          <img
            src={referralsOverTime}
            alt="Referrals Over Time"
            className="h-[128px]"
          />
        </Stack>
      )}
      <Stack fullWidth className="min-h-0 flex-1 overflow-hidden">
        <TechniciansTable
          data={rankedTechniciansWithStats}
          timeRange={timeRange}
          setTimeRange={setTimeRange}
          presets={USER_REPORTING_PRESETS}
          isLoading={isLoadingTechnicians}
          isLoadingStats={isLoadingStats}
          onRowClick={handleSelectTechnician}
          selectedTechnicianId={selectedItemId}
          className="h-full w-full"
        />
      </Stack>
    </Stack>
  );
};
